import { useNavigate } from 'react-router-dom';

// What the weight calculator worked out (the director, 15 Oct 2026): the
// totals, each line's share, and what is missing to work it out -- each gap
// with where it is filled in. Shared by the invoice form and the Weight
// calculator page.

const TEAL = '#14b8a6';
const BORD = '#2a3040';
const MUTED = '#6b7280';
const AMBER = '#f59e0b';

const group = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
export const kg = (n) => (n === null || n === undefined ? '—' : `${group(String(Math.round(n)))} kg`);
const whole = (n) => (n === null || n === undefined ? '—' : group(String(n)));
const STACK_WORDS = { carries: 'carries one on top', on_top: 'goes on top', both: 'carries one and goes on top' };
// One decimal, written as the invoice writes numbers: 2 052,6 kg.
const kg1 = (n) => { const [i, d] = (Math.round(n * 10) / 10).toFixed(1).split('.'); return `${group(i)},${d} kg`; };
// The chain, with a layer's extras after it: 322 Dose tray → 46 Euro pallet + 46 Cardboard cover sheet.
const chainOf = (packed) => packed.map(p => `${p.extra ? '+ ' : ''}${whole(p.count)} ${p.container || '(no container)'}`)
  .reduce((s, x, i) => (i === 0 ? x : s + (x.startsWith('+ ') ? ' ' : ' → ') + x), '');

// onChoose(use): another tape or foil was picked -- work it out again with it.
export default function WeightResult({ result, onUse, onChoose }) {
  const navigate = useNavigate();
  if (!result) return null;
  const complete = result.gaps.length === 0;
  const tile = (label, value, atLeast) => (
    <div style={{ background: '#161b22', border: `1px solid ${BORD}`, borderRadius: '10px', padding: '10px 12px', minWidth: '120px' }}>
      <div style={{ fontSize: '10px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: '18px', fontWeight: '800', color: value === null ? AMBER : '#f0f2f5', fontVariantNumeric: 'tabular-nums' }}>
        {value === null ? `at least ${atLeast}` : value}
      </div>
    </div>
  );
  const sel = { background: '#161b22', border: `1px solid ${BORD}`, borderRadius: '8px', padding: '6px 8px', fontSize: '12.5px', color: '#f0f2f5', minWidth: '190px' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {result.choices && onChoose && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {[['wrap', 'Wrapping foil'], ['cover', 'Cover sheets'], ['tape', 'Tape on the boxes']].map(([k, label]) => (
            <label key={k} style={{ fontSize: '10px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', flexDirection: 'column', gap: '4px' }}>{label}
              <select style={sel} value={result.use?.[k] ?? (k === 'cover' ? 'none' : '')} onChange={e => onChoose({ ...result.use, [k]: e.target.value })}>
                {k !== 'cover' && result.use?.[k] == null && <option value="">Not chosen</option>}
                {k === 'cover' && <option value="none">No cover sheets</option>}
                {(result.choices[k] || []).map(c => <option key={c.id} value={c.id}>{c.name}{c.is_default ? ' (default)' : ''}</option>)}
              </select>
            </label>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {tile('Net weight', result.net_kg === null ? null : kg(result.net_kg), kg(result.known.net_kg))}
        {tile('Gross weight', result.gross_kg === null ? null : kg(result.gross_kg), kg(result.known.gross_kg))}
        {tile('Euro pallets', result.pallets === null ? null : whole(result.pallets), whole(result.known.pallets))}
        {tile('Pallet spaces', result.pallet_spaces === null ? null : whole(result.pallet_spaces), whole(result.known.pallet_spaces))}
        {onUse && (
          <button onClick={() => onUse(result)} style={{ alignSelf: 'center', background: complete ? TEAL : 'transparent', color: complete ? '#0f172a' : TEAL, border: `1px solid ${TEAL}`, borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
            {complete ? 'Use these' : 'Use what is known'}
          </button>
        )}
      </div>

      {result.lines.length > 0 && (
        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
          Of the gross: packaging {kg1(result.lines.reduce((t, l) => t + (l.tare_kg || 0), 0))}
          {' · '}tape {kg1(result.tape_kg || 0)}
          {' · '}foil {kg1(result.foil_kg || 0)}{result.covers ? ` (${whole(result.covers)} cover sheet${result.covers === 1 ? '' : 's'})` : ''}
          {result.labels ? <>{' · '}{whole(result.labels)} label{result.labels === 1 ? '' : 's'} {kg1(result.label_kg || 0)}</> : null}
        </div>
      )}

      {!complete && (
        <div style={{ border: `1px solid ${AMBER}`, borderRadius: '10px', padding: '10px 12px', fontSize: '12.5px' }}>
          <div style={{ color: AMBER, fontWeight: 700, marginBottom: '4px' }}>
            {result.gaps.length} thing{result.gaps.length === 1 ? '' : 's'} missing to work it out fully
          </div>
          {result.gaps.map((g, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '3px 0', color: '#d1d5db' }}>
              <span>{g.says}</span>
              {g.fix && <button onClick={() => navigate(g.fix.link)} style={{ background: 'none', border: 'none', color: TEAL, fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', padding: 0 }}>{g.fix.label} →</button>}
            </div>
          ))}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', minWidth: '640px' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: MUTED, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <th style={{ padding: '5px 6px' }}>Item</th><th style={{ padding: '5px 6px' }}>Packed as</th>
              <th style={{ padding: '5px 6px', textAlign: 'right' }}>Net</th><th style={{ padding: '5px 6px', textAlign: 'right' }}>Gross</th>
              <th style={{ padding: '5px 6px', textAlign: 'right' }}>Pallets</th><th style={{ padding: '5px 6px', textAlign: 'right' }}>Spaces</th>
            </tr>
          </thead>
          <tbody>
            {result.lines.map((l, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${BORD}` }}>
                <td style={{ padding: '6px', fontWeight: 600 }}>
                  {l.name}
                  {STACK_WORDS[l.stacking] && <span style={{ fontSize: '10px', color: MUTED, marginLeft: '6px' }}>{STACK_WORDS[l.stacking]}</span>}
                </td>
                <td style={{ padding: '6px', color: '#9ca3af' }}>{chainOf(l.packed) || '—'}{l.height_mm ? <span style={{ fontSize: '10.5px', color: MUTED }}> · {whole(l.height_mm)} mm high</span> : null}</td>
                <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{kg(l.net_kg)}</td>
                <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{kg(l.gross_kg)}</td>
                <td style={{ padding: '6px', textAlign: 'right' }}>{whole(l.pallets)}</td>
                <td style={{ padding: '6px', textAlign: 'right' }}>{whole(l.pallet_spaces)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
